// ============================================================================
// NoticeRecipients — staged recipient handling for the Notice widget
// ============================================================================
// Each recipient is a compact card. The three activities are separated so the
// common path stays uncluttered:
//
//   1. Recipient Details (role / name / address) — rarely edited. Hidden from
//      the main flow; reached via the ✎ edit icon. On "Add Recipient" only the
//      detail fields are shown; saving opens the Service step.
//   2. Service (mode / dispatched-on / tracking) — shown first when the card is
//      opened. Delivery stays locked until service is recorded.
//   3. Delivery (status / date / POD) — appears only after service is recorded.
//      The recorded Service stays viewable / editable via a collapsible summary.
//
// Reads/writes the same row array shape as before, so it remains a drop-in for
// the recipients field inside NoticeSection.

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { GridFieldSpec, GridRowFieldSpec, GridRowValues, SelectOption } from 'api/types/formSpec.types';
import DynamicField from './DynamicField';
import { evalBool } from './expressionEvaluator';
import { hasSeedResolver, resolveSeed, type SeedContext } from './seedResolver';

export interface NoticeRecipientsProps {
  field: GridFieldSpec;
  value: GridRowValues[] | null | undefined;
  onChange: (next: GridRowValues[]) => void;
  error?: string;
  disabled?: boolean;
  seedContext?: SeedContext;
}

const DETAIL_KEYS   = ['party_role', 'recipient_name', 'recipient_address'];
const SERVICE_KEYS  = ['dispatch_mode', 'dispatch_date', 'tracking_no'];
const DELIVERY_KEYS = ['delivery_status', 'delivery_date', 'pod_remarks'];

const ROLE_CHIP: Record<string, { bg: string; fg: string; label: string }> = {
  PRIMARY:     { bg: '#dbeafe', fg: '#1e40af', label: 'Borrower' },
  CO_BORROWER: { bg: '#ccfbf1', fg: '#115e59', label: 'Co-Borrower' },
  GUARANTOR:   { bg: '#fef3c7', fg: '#92400e', label: 'Guarantor' },
};

const DELIVERY_FG: Record<string, string> = {
  DELIVERED: '#16a34a', SERVED: '#16a34a', DISPATCHED: '#d97706',
  RETURNED: '#dc2626', REFUSED: '#dc2626', NOT_TRACEABLE: '#dc2626', PUBLISHED: '#6b7280',
};

type OpenMode = 'details' | 'flow';

const asOptions = (raw?: Array<SelectOption | string>): SelectOption[] =>
  (raw ?? []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

const serviceRecorded = (row: GridRowValues) => !!row.dispatch_mode || !!row.dispatch_date;

const NoticeRecipients: React.FC<NoticeRecipientsProps> = ({
  field, value, onChange, error, disabled, seedContext,
}) => {
  const rows: GridRowValues[] = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [openMode, setOpenMode] = useState<OpenMode>('flow');
  // Which stage to show while a card is open in 'flow' mode. Decided ONCE when
  // the card is expanded (from the row's current status) — NOT re-derived while
  // the user types, so entering the first service field doesn't jump to Delivery.
  const [flowStage, setFlowStage] = useState<'service' | 'delivery'>('service');
  const [isNewRow, setIsNewRow] = useState(false);
  const [serviceSummaryOpen, setServiceSummaryOpen] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);

  const rowFields = field.row_fields ?? [];
  const byKeys = useCallback(
    (keys: string[]) => rowFields.filter((f) => keys.includes(f.key)),
    [rowFields],
  );
  const detailFields   = useMemo(() => byKeys(DETAIL_KEYS),   [byKeys]);
  const serviceFields  = useMemo(() => byKeys(SERVICE_KEYS),  [byKeys]);
  const deliveryFields = useMemo(() => byKeys(DELIVERY_KEYS), [byKeys]);
  const hasService     = serviceFields.length > 0;

  const optsOf = useCallback((key: string): SelectOption[] => {
    const rf = rowFields.find((f) => f.key === key) as (GridRowFieldSpec & { options?: SelectOption[] }) | undefined;
    return asOptions(rf?.options);
  }, [rowFields]);
  const roleOptions = useMemo(() => optsOf('party_role'), [optsOf]);
  const deliveryOptions = useMemo(() => optsOf('delivery_status'), [optsOf]);
  const labelFor = (opts: SelectOption[], v: unknown) =>
    opts.find((o) => o.value === v)?.label ?? (v == null || v === '' ? '' : String(v));

  const atMin = field.min_rows != null && rows.length <= field.min_rows;
  const atMax = field.max_rows != null && rows.length >= field.max_rows;
  const canSeed = !!field.seed_from && hasSeedResolver(field.seed_from);

  const closeCard = useCallback(() => { setOpenIdx(null); setIsNewRow(false); setNameError(null); setServiceSummaryOpen(false); }, []);

  const patchRow = useCallback((idx: number, key: string, val: unknown) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  }, [rows, onChange]);

  const openFlow = useCallback((idx: number) => {
    if (openIdx === idx && openMode === 'flow') { closeCard(); return; }
    // Snapshot the stage from the row's status at open time.
    const stage: 'service' | 'delivery' =
      hasService && !serviceRecorded(rows[idx] ?? {}) ? 'service' : 'delivery';
    setOpenIdx(idx); setOpenMode('flow'); setFlowStage(stage);
    setIsNewRow(false); setServiceSummaryOpen(false);
  }, [openIdx, openMode, hasService, rows, closeCard]);

  const openDetails = useCallback((idx: number) => {
    setOpenIdx(idx); setOpenMode('details'); setIsNewRow(false); setNameError(null);
  }, []);

  const addRecipient = useCallback(() => {
    if (atMax || disabled) return;
    const idx = rows.length;
    onChange([...rows, {}]);
    setOpenIdx(idx); setOpenMode('details'); setIsNewRow(true); setNameError(null);
  }, [atMax, disabled, rows, onChange]);

  const removeRecipient = useCallback((idx: number) => {
    if (disabled || atMin) return;
    onChange(rows.filter((_, i) => i !== idx));
    closeCard();
  }, [disabled, atMin, rows, onChange, closeCard]);

  const saveNewRecipient = useCallback((idx: number) => {
    const name = String(rows[idx]?.recipient_name ?? '').trim();
    if (!name) { setNameError('Recipient name is required.'); return; }
    // A fresh recipient always starts at the Service step.
    setNameError(null); setIsNewRow(false); setOpenMode('flow');
    setFlowStage(hasService ? 'service' : 'delivery'); setServiceSummaryOpen(false);
  }, [rows, hasService]);

  const cancelDetails = useCallback((idx: number) => {
    if (isNewRow) removeRecipient(idx); // discard an unsaved new recipient
    else closeCard();
  }, [isNewRow, removeRecipient, closeCard]);

  const prefill = useCallback(async () => {
    if (!field.seed_from || disabled || isSeeding) return;
    setIsSeeding(true);
    try {
      const seeded = await resolveSeed(field.seed_from, seedContext ?? {});
      const key = field.seed_dedupe_key;
      const existing = key ? new Set(rows.map((r) => r[key]).filter((v) => v != null)) : null;
      const fresh = key ? seeded.filter((r) => !existing!.has(r[key])) : seeded;
      onChange(rows.length === 0 ? seeded : [...rows, ...fresh]);
    } finally {
      setIsSeeding(false);
    }
  }, [field.seed_from, field.seed_dedupe_key, disabled, isSeeding, rows, seedContext, onChange]);

  const renderFields = (idx: number, fields: GridRowFieldSpec[], row: GridRowValues) => (
    <div style={editorGridStyle}>
      {fields
        .filter((f) => !f.show_if || evalBool(f.show_if, row, true))
        .map((f) => (
          <DynamicField
            key={f.key}
            field={f as never}
            value={row[f.key]}
            onChange={(v) => patchRow(idx, f.key, v)}
            disabled={disabled}
          />
        ))}
    </div>
  );

  return (
    <div data-field={field.key}>
      {/* Toolbar */}
      <div style={toolbarStyle}>
        <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
          {rows.length} recipient{rows.length === 1 ? '' : 's'}{field.max_rows ? ` · max ${field.max_rows}` : ''}
        </div>
        {!disabled && (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {canSeed && (
              <button type="button" className="btn btn-secondary" style={smallBtn} onClick={() => void prefill()} disabled={isSeeding}>
                <IconSystem.refresh size={12} style={{ marginRight: '0.3rem' }} />
                {isSeeding ? 'Loading…' : (field.seed_label ?? 'Pre-fill from account')}
              </button>
            )}
            {!atMax && (
              <button type="button" className="btn btn-secondary" style={smallBtn} onClick={addRecipient}>
                <IconSystem.add size={12} style={{ marginRight: '0.3rem' }} />
                {field.add_label ?? 'Add Recipient'}
              </button>
            )}
          </div>
        )}
      </div>

      {rows.length === 0 && (
        <div style={emptyStyle}>
          {field.empty_message ?? 'No recipients yet. Use "Pre-fill from account" to populate from the loan.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.map((row, idx) => {
          const role = String(row.party_role ?? '');
          const chip = ROLE_CHIP[role] ?? { bg: 'var(--bg-secondary)', fg: 'var(--text-secondary)', label: labelFor(roleOptions, role) || 'Recipient' };
          const isOpen = openIdx === idx;
          const svcDone = serviceRecorded(row);
          const delivVal = String(row.delivery_status ?? '');

          return (
            <div key={idx} style={{ ...cardStyle, ...(isOpen ? cardOpenStyle : null) }}>
              {/* Header */}
              <div style={rowHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0, flex: 1 }}>
                  <span style={{ ...roleChipStyle, background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={nameStyle}>
                      {String(row.recipient_name ?? '') || <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Unnamed recipient</span>}
                    </div>
                    {row.recipient_address ? <div style={addrStyle}>{String(row.recipient_address)}</div> : null}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                  {/* Status */}
                  {delivVal ? (
                    <span style={{ ...statusChip, color: DELIVERY_FG[delivVal] ?? 'var(--text-secondary)', border: `1px solid ${DELIVERY_FG[delivVal] ?? 'var(--border-color)'}` }}>
                      {labelFor(deliveryOptions, delivVal) || delivVal}
                    </span>
                  ) : svcDone ? (
                    <span style={{ ...statusChip, color: '#d97706', border: '1px solid #d97706' }}>Dispatched</span>
                  ) : (
                    <span style={{ ...statusChip, color: 'var(--text-secondary)', border: '1px dashed var(--border-color)' }}>Not served</span>
                  )}
                  {!disabled && (
                    <>
                      <button type="button" style={iconBtn} title="Edit recipient details" onClick={() => (isOpen && openMode === 'details' ? closeCard() : openDetails(idx))}>
                        <IconSystem.edit size={13} />
                      </button>
                      <button type="button" style={iconBtn} title={svcDone ? 'Update delivery' : 'Record service'} onClick={() => openFlow(idx)}>
                        <IconSystem.chevronDown size={14} style={{ transform: isOpen && openMode === 'flow' ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                      </button>
                      <button type="button" style={{ ...iconBtn, color: '#dc2626' }} title={atMin ? `At least ${field.min_rows} required` : 'Remove recipient'} onClick={() => removeRecipient(idx)} disabled={atMin}>
                        <IconSystem.trash size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* ---- Recipient Details editor (edit icon / add flow) ---- */}
              {isOpen && openMode === 'details' && (
                <div style={editorStyle}>
                  <div style={subLabel}>{isNewRow ? 'New Recipient' : 'Recipient Details'}</div>
                  {renderFields(idx, detailFields, row)}
                  {nameError && <div className="form-error-message" style={{ marginTop: '0.3rem' }}>{nameError}</div>}
                  <div style={editorActions}>
                    <button type="button" className="btn btn-secondary" style={smallBtn} onClick={() => cancelDetails(idx)}>
                      {isNewRow ? 'Discard' : 'Close'}
                    </button>
                    {isNewRow ? (
                      <button type="button" className="btn btn-primary" style={smallBtn} onClick={() => saveNewRecipient(idx)}>
                        Save &amp; Record Service
                      </button>
                    ) : (
                      <button type="button" className="btn btn-primary" style={smallBtn} onClick={closeCard}>Done</button>
                    )}
                  </div>
                </div>
              )}

              {/* ---- Service / Delivery flow (stage fixed at open time) ---- */}
              {isOpen && openMode === 'flow' && (
                <div style={editorStyle}>
                  {flowStage === 'service' ? (
                    <>
                      <div style={subLabel}>Service</div>
                      {renderFields(idx, serviceFields, row)}
                      <div style={hintStyle}>Enter the dispatch details, then click Done. Re-open this recipient to record delivery.</div>
                    </>
                  ) : (
                    <>
                      <div style={subLabel}>Delivery</div>
                      {renderFields(idx, deliveryFields, row)}

                      {hasService && (
                        <div style={{ marginTop: '0.6rem', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                          <button type="button" onClick={() => setServiceSummaryOpen((o) => !o)} style={summaryHeader}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                              <IconSystem.chevronDown size={13} style={{ transform: serviceSummaryOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s', color: 'var(--text-secondary)' }} />
                              Service — recorded
                            </span>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                              {[labelFor(optsOf('dispatch_mode'), row.dispatch_mode), row.dispatch_date ? String(row.dispatch_date) : ''].filter(Boolean).join(' · ') || 'view / edit'}
                            </span>
                          </button>
                          {serviceSummaryOpen && (
                            <div style={{ padding: '0.25rem 0.6rem 0.6rem' }}>
                              {renderFields(idx, serviceFields, row)}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <div style={editorActions}>
                    <button type="button" className="btn btn-secondary" style={smallBtn} onClick={closeCard}>Done</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className="form-error-message" style={{ marginTop: '0.4rem' }}>{error}</div>}
    </div>
  );
};

// ---- Styles ----
const toolbarStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' };
const smallBtn: React.CSSProperties = { padding: '0.25rem 0.65rem', fontSize: '0.78rem' };
const emptyStyle: React.CSSProperties = {
  padding: '0.9rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem', fontStyle: 'italic',
  background: 'var(--bg-secondary)', border: '1px dashed var(--form-border-color)', borderRadius: 6,
};
const cardStyle: React.CSSProperties = { border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--bg-primary)', overflow: 'hidden' };
const cardOpenStyle: React.CSSProperties = { borderColor: '#2563eb', boxShadow: '0 0 0 1px #2563eb22' };
const rowHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', padding: '0.55rem 0.7rem' };
const roleChipStyle: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 };
const nameStyle: React.CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const addrStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '38ch' };
const statusChip: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 600, padding: '0.08rem 0.45rem', borderRadius: 999, whiteSpace: 'nowrap' };
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' };
const editorStyle: React.CSSProperties = { padding: '0.2rem 0.7rem 0.7rem', borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)' };
const subLabel: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', margin: '0.6rem 0 0.45rem' };
const editorGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.55rem 0.75rem' };
const editorActions: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.7rem' };
const hintStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.45rem' };
const summaryHeader: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.45rem 0.6rem', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-primary)' };

export default NoticeRecipients;
